import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

const defaults = {
  spin_enabled: true,
  spin_disabled_message: "عجلة العروض متوقفة مؤقتاً. يرجى المراجعة لاحقاً.",
  spins_per_phone: 1,
  days_between_spins: 30,
  default_coupon_validity_days: 30,
  prevent_multiple_active_coupons: true,
  min_invoice_amount: 0,
  campaign_name: "حملة مغاسل القمر الأبيض",
  campaign_start: "",
  campaign_end: "",
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(defaults);
  const [settingsId, setSettingsId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { lang } = useApp();
  const tr = t[lang];

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    const list = await base44.entities.Settings.list();
    if (list[0]) {
      setSettings({ ...defaults, ...list[0] });
      setSettingsId(list[0].id);
    }
    setLoading(false);
  };

  const set = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    if (settingsId) {
      await base44.entities.Settings.update(settingsId, settings);
    } else {
      const created = await base44.entities.Settings.create(settings);
      setSettingsId(created.id);
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (loading) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="flex justify-center py-16">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-black text-foreground">⚙️ {tr.settingsTitle}</h1>
            <p className="text-muted-foreground text-sm">{lang === "ar" ? "إعدادات نظام عجلة العروض" : "Spin wheel system settings"}</p>
          </div>

          {/* Wheel Status */}
          <div className={`rounded-2xl p-6 border shadow-sm ${settings.spin_enabled ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-black text-lg text-foreground">
                  {settings.spin_enabled ? `🟢 ${lang === "ar" ? "العجلة مفعّلة" : "Wheel Enabled"}` : `🔴 ${lang === "ar" ? "العجلة موقوفة" : "Wheel Disabled"}`}
                </h3>
                <p className="text-sm text-muted-foreground">{lang === "ar" ? "التحكم في تشغيل عجلة العروض" : "Control the spin wheel operation"}</p>
              </div>
              <button
                onClick={() => set("spin_enabled", !settings.spin_enabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${settings.spin_enabled ? "bg-green-500" : "bg-red-400"}`}
              >
                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${settings.spin_enabled ? "right-1" : "left-1"}`}></span>
              </button>
            </div>
            {!settings.spin_enabled && (
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.spinDisabledMessage}</label>
                <textarea
                  value={settings.spin_disabled_message}
                  onChange={e => set("spin_disabled_message", e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none bg-background text-foreground"
                  placeholder={lang === "ar" ? "الرسالة التي تظهر للعميل عند إيقاف العجلة" : "Message shown to customers when wheel is disabled"}
                />
              </div>
            )}
          </div>

          {/* Campaign Info */}
          <div className="bg-card rounded-2xl p-6 border border-border shadow-sm space-y-4">
            <h3 className="font-bold text-foreground flex items-center gap-2">🎯 {lang === "ar" ? "معلومات الحملة" : "Campaign Info"}</h3>
            <div>
              <label className="block text-sm font-bold mb-1 text-foreground">{tr.campaignName}</label>
              <input
                type="text"
                value={settings.campaign_name}
                onChange={e => set("campaign_name", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.campaignStart}</label>
                <input type="date" value={settings.campaign_start || ""} onChange={e => set("campaign_start", e.target.value)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.campaignEnd}</label>
                <input type="date" value={settings.campaign_end || ""} onChange={e => set("campaign_end", e.target.value)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground" />
              </div>
            </div>
          </div>

          {/* Spin Limits */}
          <div className="bg-card rounded-2xl p-6 border border-border shadow-sm space-y-4">
            <h3 className="font-bold text-foreground flex items-center gap-2">🔢 {lang === "ar" ? "قيود اللف" : "Spin Limits"}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.spinsPerPhone}</label>
                <input type="number" min={1} value={settings.spins_per_phone} onChange={e => set("spins_per_phone", parseInt(e.target.value) || 1)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.daysBetweenSpins}</label>
                <input type="number" min={1} value={settings.days_between_spins} onChange={e => set("days_between_spins", parseInt(e.target.value) || 30)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground" />
                <p className="text-xs text-muted-foreground mt-1">{lang === "ar" ? "مثال: 30 يعني لفة كل 30 يوم" : "e.g. 30 means one spin every 30 days"}</p>
              </div>
            </div>
          </div>

          {/* Coupon Settings */}
          <div className="bg-card rounded-2xl p-6 border border-border shadow-sm space-y-4">
            <h3 className="font-bold text-foreground flex items-center gap-2">🎫 {lang === "ar" ? "إعدادات الكوبونات" : "Coupon Settings"}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.defaultValidity}</label>
                <input type="number" min={1} value={settings.default_coupon_validity_days} onChange={e => set("default_coupon_validity_days", parseInt(e.target.value) || 30)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1 text-foreground">{tr.minInvoice}</label>
                <input type="number" min={0} value={settings.min_invoice_amount} onChange={e => set("min_invoice_amount", parseFloat(e.target.value) || 0)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary bg-background text-foreground" />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
              <div>
                <p className="font-medium text-sm text-foreground">{tr.preventMultiple}</p>
                <p className="text-xs text-muted-foreground">{lang === "ar" ? "يجب أن يستخدم العميل كوبونه قبل لف العجلة مرة أخرى" : "Customer must use their coupon before spinning again"}</p>
              </div>
              <button
                onClick={() => set("prevent_multiple_active_coupons", !settings.prevent_multiple_active_coupons)}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${settings.prevent_multiple_active_coupons ? "bg-primary" : "bg-gray-400"}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${settings.prevent_multiple_active_coupons ? "right-1" : "left-1"}`}></span>
              </button>
            </div>
          </div>

          {/* Save */}
          <div className="flex gap-3">
            {saved && (
              <div className="flex-1 py-3 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-center font-bold rounded-xl">
                ✅ {tr.settingsSaved}
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-3.5 bg-primary text-primary-foreground font-black rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60 text-lg"
            >
              {saving ? `⌛ ${tr.saving}` : `💾 ${tr.saveSettings}`}
            </button>
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}